import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastProvider } from "./components/Toast";
import ErrorBoundary from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import DashboardLayout from "./layouts/DashboardLayout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import WidgetsList from "./pages/WidgetsList";
import WidgetForm from "./pages/WidgetForm";
import KnowledgeManager from "./pages/KnowledgeManager";
import WidgetAnalytics from "./pages/WidgetAnalytics";
import ConversationsViewer from "./pages/ConversationsViewer";
import Settings from "./pages/Settings";
import AdminPlans from "./pages/AdminPlans";
import AdminCredits from "./pages/AdminCredits";
import AcceptInvite from "./pages/AcceptInvite";
import { LandingPage } from "./pages/LandingPage";
import { PublicRoute } from "./components/PublicRoute";
import { DefaultRedirect } from "./components/DefaultRedirect";
import { AnalyticsRedirect } from "./components/AnalyticsRedirect";
import { ROUTES } from "./config/constants";
import authService from "./services/auth";

function App() {
  // If localStorage has a user entry, verify the session is still valid before
  // rendering routes. This prevents PublicRoute from redirecting an
  // "authenticated" user (stale localStorage) to /dashboard, where the first
  // API call would 401, triggering a redirect to /login — making / appear to
  // redirect to /login. verifyToken() clears local auth on 401/403 so that by
  // the time routes render, isAuthenticated() reflects true session state.
  //
  // Fresh visitors (no localStorage entry) skip the network call entirely and
  // render immediately. Network errors preserve the session so users aren't
  // logged out by a brief outage.
  const [authChecked, setAuthChecked] = useState(
    !authService.isAuthenticated(),
  );

  useEffect(() => {
    if (!authChecked) {
      authService.verifyToken().finally(() => setAuthChecked(true));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authChecked) {
    return null;
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <Routes>
            {/* Public-only routes — authenticated users are redirected to
                ROUTES.AUTHENTICATED_HOME (PublicRoute default) so they never
                land on auth/marketing pages. */}
            <Route
              path={ROUTES.LANDING}
              element={
                <PublicRoute>
                  <LandingPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.LOGIN}
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.REGISTER}
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.FORGOT_PASSWORD}
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            {/* /reset-password and /verify-email are reached via email links and
                must work regardless of auth state (e.g. user opens link in a
                browser where they happen to be logged in). */}
            <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
            <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />

            {/* Protected Routes with Dashboard Layout — pathless so it acts as
                a layout wrapper only; child paths claim their own segments */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="widgets" element={<WidgetsList />} />
              <Route path="widgets/new" element={<WidgetForm />} />
              <Route path="widgets/:id/edit" element={<WidgetForm />} />
              <Route
                path="widgets/:id/knowledge"
                element={<KnowledgeManager />}
              />
              <Route
                path="widgets/:id/analytics"
                element={<WidgetAnalytics />}
              />
              <Route
                path="widgets/:id/conversations"
                element={<ConversationsViewer />}
              />
              <Route
                path="widgets/:id/conversations/:conversationId"
                element={<ConversationsViewer />}
              />
              <Route path="settings" element={<Settings />} />
              <Route path="invite" element={<AcceptInvite />} />
              {/* Admin routes — ProtectedRoute checks role client-side; server enforces via requireRole("admin") */}
              <Route
                path="admin/plans"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminPlans />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/credits"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminCredits />
                  </ProtectedRoute>
                }
              />
              {/* Legacy route redirect */}
              <Route path="analytics" element={<AnalyticsRedirect />} />
            </Route>

            {/* Catch all — DefaultRedirect owns the routing policy for unmatched
                paths: authenticated → dashboard, unauthenticated → landing page. */}
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
